import React, { useState, useRef, useEffect, Component, useMemo, useCallback } from "react";
import "./VoiceRecorder.css";
import VADDetector from "../api/vad";
import processingMusic from "../assests/processing_music.mp3";
import {
  voiceTranslate,
  OtherLanguageAsr,
  translateVoice,
  asrSarvam,
  translateSarvam,
  ttsSarvam,
  ttsBani,
  masterOrchestration,
} from "../api/voiceApi";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:4345" : "");


class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, errorMessage: err?.message || "Unknown error" };
  }

  componentDidCatch(err, info) {
    console.error("[ErrorBoundary]", err, info);
  }

  handleReset = () => {
    try {
      sessionStorage.removeItem("vr_session_id");
    } catch (_) { }
    this.setState({ hasError: false, errorMessage: "" });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            gap: "16px",
            fontFamily: "sans-serif",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "48px" }}></span>
          <h2 style={{ margin: 0 }}>Something went wrong</h2>
          <p style={{ color: "#666", maxWidth: "480px" }}>
            {this.state.errorMessage}
          </p>
          <button
            onClick={this.handleReset}
            style={{
              padding: "10px 24px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "15px",
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const isDev = process.env.NODE_ENV === "development";
const log = (...a) => isDev && console.log(...a);
const warn = (...a) => isDev && console.warn(...a);
const err = (...a) => console.error(...a);

const MAX_TEXT_LENGTH = 2000;

function sanitizeText(raw) {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/<[^>]*>/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[\u200B-\u200F\u202A-\u202E\uFEFF]/g, "")
    .trim()
    .slice(0, MAX_TEXT_LENGTH);
}

const SPEAK_AGAIN_MAP = {
  "en": "Please speak again, I could not understand your audio.",
  "en-IN": "Please speak again, I could not understand your audio.",
  "hi": "कृपया फिर से बोलें, मुझे आपकी आवाज़ समझ नहीं आई।",
  "hi-IN": "कृपया फिर से बोलें, मुझे आपकी आवाज़ समझ नहीं आई।",
  "te": "దయచేసి మళ్లీ మాట్లాడండి, మీ మాటలు నాకు అర్థం కాలేదు.",
  "te-IN": "దయచేసి మళ్లీ మాట్లాడండి, మీ మాటలు నాకు అర్థం కాలేదు.",
  "or": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
  "or-IN": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
  "od-IN": "ଦୟାକରି ପୁଣି ଥରେ କୁହନ୍ତୁ, ମୁଁ ଆପଣଙ୍କ କଥା ବୁଝିପାରିଲି ନାହିଁ ।",
};

function generateUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  a[6] = (a[6] & 0x0f) | 0x40;
  a[8] = (a[8] & 0x3f) | 0x80;
  return Array.from(a, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5");
}

function createAudioUrlFromBase64(base64Data) {
  const chars = atob(base64Data);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);

  let mimeType = "audio/mp3";
  if (bytes.length >= 4) {
    if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) mimeType = "audio/mp3"; // ID3
    else if (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) mimeType = "audio/mp3"; // ADTS MP3
    else if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) mimeType = "audio/wav"; // RIFF (WAV)
    else if (bytes.length >= 8 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) mimeType = "audio/mp4"; // ftyp (MP4/AAC)
    else if (bytes[0] === 0x1A && bytes[1] === 0x45 && bytes[2] === 0xDF && bytes[3] === 0xA3) mimeType = "audio/webm"; // EBML (WebM)
    else if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) mimeType = "audio/ogg"; // OggS
  }

  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

function revokeAudioUrl(url) {
  try {
    if (url) URL.revokeObjectURL(url);
  } catch (_) { }
}

async function executeFetchAttempt(
  url,
  options,
  isValidResponse,
  attempt,
  maxRetries,
  timeoutMs
) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    log(`fetchWithRetry: attempt ${attempt}/${maxRetries} → ${url}`);
    const res = await fetch(url, { ...options, signal: ctrl.signal });
    clearTimeout(id);
    if (!res.ok) {
      warn(`fetchWithRetry: HTTP ${res.status} on attempt ${attempt}`);
      return { success: false, retry: attempt < maxRetries };
    }
    const data = await res.json().catch(() => null);
    if (!data || !isValidResponse(data)) {
      warn(`fetchWithRetry: invalid response on attempt ${attempt}`, data);
      return { success: false, retry: attempt < maxRetries };
    }
    return { success: true, data };
  } catch (e) {
    clearTimeout(id);
    warn(`fetchWithRetry: ${e.name === "AbortError" ? "timeout" : "error"} on attempt ${attempt}`, e);
    return { success: false, retry: attempt < maxRetries };
  }
}

async function fetchWithRetry(
  url,
  options,
  isValidResponse,
  maxRetries = 3,
  delayMs = 1000,
  timeoutMs = 60000
) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await executeFetchAttempt(
      url,
      options,
      isValidResponse,
      attempt,
      maxRetries,
      timeoutMs
    );
    if (result.success) return result.data;
    if (result.retry) await new Promise((r) => setTimeout(r, delayMs));
  }
  err(`fetchWithRetry: all ${maxRetries} attempts failed for ${url}`);
  return null;
}

const isAskResponseValid = (data) => {
  const ask = data?.ask || data;
  return !!(
    ask?.original_text ||
    ask?.sarvam_text ||
    ask?.bhashini_text ||
    ask?.text ||
    ask?.message ||
    ask?.response ||
    ask?.sarvam_audio_base64 ||
    ask?.bhashini_audio_base64 ||
    ask?.audio_base64
  );
};


async function callTranslationWithRetry(formData, normalizedLang) {
  const result = await translateVoice(formData, normalizedLang);
  log("callTranslationWithRetry result:", result);
  if (result?.error) { err("Translation error:", result.error); return result; }
  return result;
}

async function validateWebMBlob(blob) {
  const buf = await blob.slice(0, 4).arrayBuffer();
  const h = new Uint8Array(buf);
  return h[0] === 0x1a && h[1] === 0x45 && h[2] === 0xdf && h[3] === 0xa3;
}

function extractTranscriptText(d) {
  if (!d) return "";
  if (d?.translation) {
    if (typeof d.translation === "string") return d.translation;
    if (typeof d.translation === "object") {
      return (
        d.translation.asr ||
        d.translation.transcript ||
        d.translation.text ||
        ""
      );
    }
  }
  return d?.transcript || d?.asr || d?.text || "";
}

async function fetchSarvamTranslation(audioBlob, normalizedLang) {
  log("Calling Sarvam ASR, blob size:", audioBlob.size, "lang:", normalizedLang);
  const asrRes = await asrSarvam(audioBlob, normalizedLang);
  const asrText = asrRes?.transcript || asrRes?.asr || (typeof asrRes === "string" ? asrRes : "");
  if (!asrText) {
    return { error: "Empty ASR", emptyASR: true };
  }
  if (normalizedLang === "en" || normalizedLang === "en-IN")
    return { translation: { asr: asrText, translation: asrText } };
  // const transRes = await translateSarvam(asrText, "en", normalizedLang);
  return { translation: { asr: asrText } };
}

async function fetchBaaniTranslation(audioBlob, normalizedLang) {
  log("Calling Baani ASR - lang:", normalizedLang);
  const formData = new FormData();
  formData.append("file", audioBlob, "recording.webm");
  // return voiceTranslate(formData,true)

  formData.append("lang", normalizedLang);
  const isEng = normalizedLang === "en" || normalizedLang === "en-IN";
  return isEng ? voiceTranslate(formData, true) :OtherLanguageAsr(formData, true);
}

async function processAudioTranslation(audioBlob, langConfig, model) {
  const normalizedLang = langConfig.code;
  let translationData = null;

  if (model === "sarvam") {
    translationData = await fetchSarvamTranslation(audioBlob, normalizedLang);
  } else if (model === "baani") {
    translationData = await fetchBaaniTranslation(audioBlob, normalizedLang);
  } else {
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");
    formData.append("lang", normalizedLang);
    translationData = await callTranslationWithRetry(formData, langConfig.backend);
  }

  if (!translationData || translationData.error)
    return { error: translationData?.error || "Invalid translation response" };

  return { transcriptText: extractTranscriptText(translationData) };
}

const LANG_MAP = {
  "en-IN": { code: "en", backend: "en", locale: "en-IN", name: "English" },
  "te-IN": { code: "te", backend: "te", locale: "te-IN", name: "తెలుగు" },
  "hi-IN": { code: "hi", backend: "hi", locale: "hi-IN", name: "हिन्दी" },
  "or-IN": { code: "or", backend: "or", locale: "or-IN", name: "ଓଡ଼ିଆ" },
};
const SARVAM_LANG_MAP = {
  "en-IN": { code: "en-IN", backend: "en", locale: "en-IN", name: "English" },
  "te-IN": { code: "te-IN", backend: "te", locale: "te-IN", name: "తెలుగు" },
  "hi-IN": { code: "hi-IN", backend: "hi", locale: "hi-IN", name: "हिन्दી" },
  "od-IN": { code: "od-IN", backend: "or", locale: "od-IN", name: "ଓଡ଼ିଆ" },
};
const MODEL_MAP = {
  sarvam: { code: "sarvam", name: "Sarvam" },
  baani: { code: "baani", name: "BAANI" },
};

const MOBILE_NUMBER = {
  "8179445643": "",
  "6305251679": "",
  "9676650473": "",
  "7008279837": "matiapada",
  "7008385951": "Jhirpani",
  "8260522809": "Karmabahal",
  "8658925693": "Chuabahala"
};

function extractBotResponse(data, model) {
  const bot = data?.ask || data;
  let responseText =
    bot?.original_text ||
    bot?.advice_text ||
    bot?.text ||
    bot?.message ||
    bot?.response ||
    "Please Repeat Your Response.";
  let audioBase64 = bot?.audio_base64;
  const originalText = (
    bot?.original_text ||
    bot?.text ||
    bot?.message ||
    bot?.response ||
    ""
  ).toLowerCase();

  if (model === "sarvam") {
    responseText = bot?.sarvam_text || responseText;
    audioBase64 = bot?.sarvam_audio_base64 || audioBase64;
  } else if (model === "baani") {
    responseText = bot?.bhashini_text || responseText;
    audioBase64 = bot?.bhashini_audio_base64 || audioBase64;
  }
  return { responseText, audioBase64, originalText, botResponseAudio: bot?.audio_base64 };
}

function checkIsEnding(originalText) {
  if (!originalText) return false;

  const text = originalText.toLowerCase();
  const endPhrases = [
    // English
    'thank you for calling krushi samrudhi helpline! you can call again at any time.',
    'thank you for calling krushi samrudhi helpline',
    'i am connecting you to our representative',
    'i am connecting you with a representative',
    'for further assistance, i am connecting you',
    'sorry, i could not understand. for more assistance, i am connecting you to our representative, please wait.',
    'sorry, i could not understand. for further assistance, i am connecting you to our representative, please wait.',
    "sorry, i didn't understand. please wait while i connect you with a representative for further assistance.",
    "currently, we are only accepting bookings during the 18-hour heat period. please wait, i am connecting you to our representative.",
    'please wait until then',
    'sorry, we currently only accept bookings for cows within the heat time window. please wait while i connect you to live agent for further assistance.',
    'sorry, there was a problem processing your booking. your call is being transferred to our representative for assistance.',

    // Odia
    'କୃଷି ସମୃଦ୍ଧି ହେଲ୍ପଲାଇନ୍ କୁ କଲ୍ କରିଥିବାରୁ ଧନ୍ୟବାଦ, ଆପଣ ଯେକୌଣସି ସମୟରେ ପୁଣି କଲ୍ କରିପାରିବେ!',
    'କୃଷି ସମୃଦ୍ଧି ହେଲ୍ପଲାଇନ୍ କୁ କଲ୍ କରିଥିବାରୁ ଧନ୍ୟବାଦ',
    'କ୍ଷମା କରିବେ, ଆମେ ବର୍ତ୍ତମାନ କେବଳ ଆପଣଙ୍କ ଗାଈର 18 ଘଣ୍ଟା ଉତ୍ତାପ ଅବଧି ସମୟରେ ବୁକିଂ ଗ୍ରହଣ କରୁଛୁ। ଅଧିକ ସହାୟତା ପାଇଁ ଦୟାକରି କିଛି ସମୟ ଅପେକ୍ଷା କରନ୍ତୁ, ମୁଁ ଆପଣଙ୍କୁ ଆମ ପ୍ରତିନିଧିଙ୍କ ସହ ସଂଯୋଗ କରା ଉଛି।',
    'କ୍ଷମା କରିବେ, ମୁଁ ବୁଝିପାରିଲି ନାହିଁ। ଦୟାକରି ଅଧିକ ସହାୟତା ପାଇଁ ମୁଁ ଆପଣଙ୍କୁ ପ୍ରତିନିଧିଙ୍କ ସହ ସଂଯୋଗ କରାଉଛି , ସେ ପର୍ଯ୍ୟନ୍ତ ଦୟାକରି ଅପେକ୍ଷା କରନ୍ତୁ।',
    'କ୍ଷମା କରିବେ, ଆପଣଙ୍କ ବୁକିଂ ପ୍ରକ୍ରିୟାକରଣ ସମୟରେ ଏକ ସମସ୍ୟା ହୋଇଥିଲା। ସହାୟତା ପାଇଁ, ଆପଣଙ୍କ କଲ୍ ଆମ ପ୍ରତିନିଧିଙ୍କୁ ସ୍ଥାନାନ୍ତର କରାଯାଉଛି।',

    // Hindi
    'कृषि समृद्धि हेल्पलाइन पर कॉल करने के लिए धन्यवाद! आप कभी भी दोबारा कॉल कर सकते हैं।',
    'कृषि समृद्धि हेल्पलाइन पर कॉल करने के लिए धन्यवाद',
    'क्षमा करें, हम अभी केवल 18 घंटे की हीट अवधि के दौरान बुकिंग स्वीकार कर रहे हैं। अधिक सहायता के लिए कृपया प्रतीक्षा करें, मैं आपको हमारे प्रतिनिधि से जोड़ रहा हूँ।',
    'क्षमा करें, मैं समझ नहीं पाया। अधिक सहायता के लिए मैं आपको हमारे प्रतिनिधि से जोड़ रहा हूँ, कृपया प्रतीक्षा करें।',
    'क्षमा करें, आपकी बुकिंग प्रक्रिया में एक समस्या हुई। सहायता के लिए आपकी कॉल हमारे प्रतिनिधि को स्थानांतरित की जा रही है।',

    // Telugu
    'కృషి సమృద్ధి హెల్ప్‌లైన్‌కు కాల్ చేసినందుకు ధన్యవాదాలు! మీరు ఎప్పుడైనా మళ్ళీ కాల్ చేయవచ్చు.',
    'కృషి సమృద్ధి హెల్ప్‌లైన్‌కు కాల్ చేసినందుకు ధన్యవాదాలు',
    'క్షమించండి, మేము ప్రస్తుతం 18 గంటల హీట్ వ్యవధిలో మాత్రమే బుకింగ్ స్వీకరిస్తున్నాము. మరింత సహాయం కోసం దయచేసి వేచి ఉండండి, నేను మిమ్మల్ని మా ప్రతినిధికి కనెక్ట్ చేస్తున్నాను.',
    'క్షమించండి, నాకు అర్థం కాలేదు. మరింత సహాయం కోసం నేను మిమ్మల్ని మా ప్రతినిధికి కనెక్ట్ చేస్తున్నాను, దయచేసి వేచి ఉండండి.',
    'క్షమించండి, మీ బుకింగ్ ప్రక్రియలో సమస్య ఏర్పడింది. సహాయం కోసం మీ కాల్ మా ప్రతినిధికి బదిలీ చేయబడుతోంది.',
  ];

  return endPhrases.some((phrase) => text.includes(phrase));
}
function checkIsShortReply(asrText) {
  const cleaned = (asrText || "").trim().replace(/[.,!?।]/g, "");
  const normalized = cleaned.toLowerCase();
  const words = normalized.split(/\s+/).filter(Boolean);
  const uniqueWords = [...new Set(words)];
  const collapsed = uniqueWords.join(" ");

  const replyMap = {
    yes: "yes", yup: "yes", no: "no", nope: "no",
    అవును: "yes", అవను: "yes", కాదు: "no", లేదు: "no", సరే: "yes",
    "హాँ": "yes", "नहीं": "no", "ठीक": "yes",
    "ହଁ": "yes", "ନା": "no", "ଠିକ": "yes",
  };

  let mapped = replyMap[cleaned] || replyMap[collapsed] || replyMap[normalized] || collapsed;
  const yesV = ["yes", "yeah", "yep", "yup", "ok", "okay", "correct", "right", "sure", "fine", "yas", "yess"];
  const noV = ["no", "nope", "nah", "noo", "nop"];
  if (yesV.includes(mapped)) mapped = "yes";
  else if (noV.includes(mapped)) mapped = "no";

  if (uniqueWords.length > 1 && !yesV.includes(mapped) && !noV.includes(mapped)) return false;
  return ["yes", "no"].includes(mapped.trim());
}

function parseYesNoReply(asrText) {
  const cleaned = (asrText || "").trim().replace(/[.,!?।]/g, "");
  const normalized = cleaned.toLowerCase();
  const words = normalized.split(/\s+/).filter(Boolean);
  const uniqueWords = [...new Set(words)];
  const collapsed = uniqueWords.join(" ");

  const replyMap = {
    yes: "yes", yup: "yes", no: "no", nope: "no",
    అవును: "yes", అవను: "yes", కాదు: "no", లేదు: "no", సరే: "yes",
    "హాँ": "yes", "नहीं": "no", "ठीक": "yes",
    "ହଁ": "yes", "ନା": "no", "ଠିକ": "yes",
  };

  let mapped = replyMap[cleaned] || replyMap[collapsed] || replyMap[normalized] || collapsed;
  const yesV = ["yes", "yeah", "yep", "yup", "ok", "okay", "correct", "right", "sure", "fine", "yas", "yess"];
  const noV = ["no", "nope", "nah", "noo", "nop"];
  if (yesV.includes(mapped)) mapped = "yes";
  else if (noV.includes(mapped)) mapped = "no";

  return ["yes", "no"].includes(mapped.trim()) ? mapped.trim() : null;
}

const WS_MAX_RETRIES = 5;
const WS_BASE_DELAY_MS = 1000;

function buildWsUrl() {
  if (API_BASE_URL && API_BASE_URL.startsWith("http")) {
    const parsed = new URL(API_BASE_URL);
    const proto = parsed.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${parsed.host}/ws/audio`;
  }
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const devHost = "localhost:8000";
  const host = window.location.hostname === "localhost" ? devHost : window.location.host;
  return `${proto}://${host}/ws/audio`;
}



const VoiceRecorderInner = ({ autoStart = false }) => {
  const [, setTranscript] = useState("");
  const [language, setLanguage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [, setVadStatus] = useState("idle");
  const [model, setModel] = useState("");
  const [showSpeakAgain, setShowSpeakAgain] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [replayingIndex, setReplayingIndex] = useState(null);
  const [selectedMobileNumber, setSelectedMobileNumber] = useState("");
  const [inputText, setInputText] = useState("");
  const [inputMode, setInputMode] = useState("voice");
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [isSessionCompleted, setIsSessionCompleted] = useState(false);
  const [awaitingThankyouConfirmation, setAwaitingThankyouConfirmation] = useState(false);

  const [isPaused, setIsPaused] = useState(false);
  const [pausedIndex, setPausedIndex] = useState(null);
  const awaitingThankyouConfirmationRef = useRef(false);
  const disableInputRef = useRef(false);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentStreamRef = useRef(null);
  const recordingProcessedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const vadDetectorRef = useRef(null);
  const sessionIdRef = useRef(null);
  const passiveListeningRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  const isDoneRef = useRef(false);
  const chatHistoryRef = useRef([]);
  const wsRef = useRef(null);
  const wsReadyRef = useRef(false);
  const wsReconnectAttemptsRef = useRef(0);
  const wsReconnectTimerRef = useRef(null);
  const preBufferRef = useRef([]);
  const preBufferSentRef = useRef(false);
  const isSendingAudioRef = useRef(false);
  const chatContainerRef = useRef(null);
  const playbackPromiseRef = useRef(Promise.resolve());
  const inputModeRef = useRef("voice");
  const isRecorderReadyRef = useRef(false);
  const isStoppingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const isAudioPlayingRef = useRef(false);
  const playingCountRef = useRef(0);
  const recursionDepthRef = useRef(0);
  const followupRetryRef = useRef(0);
  const appStateRef = useRef("idle");
  const isMountedRef = useRef(true);
  const replayUrlRef = useRef(null);
  const wsAudioRef = useRef(null);
  const currentAudioRef = useRef(null);
  const currentReplayAudioRef = useRef(null);

  // Background music refs
  const bgMusicRef = useRef(null);
  const bgMusicVolumeRef = useRef(0.3);
  const hasStartedBgMusicRef = useRef(false);
  const fadeIntervalRef = useRef(null);

  const isMissingSelection = !model || !language || !selectedMobileNumber;
  const isDisabledMic = isMissingSelection || isAudioPlaying || isProcessing || isDone;
  const isStartSessionDisabled = isProcessing || isMissingSelection || isDone;
  const isTextSubmitDisabled = isProcessing || isAudioPlaying || !inputText.trim() || isMissingSelection || isDone;


  const ACTIVE_LANG_MAP = useMemo(() => (model === "sarvam" ? SARVAM_LANG_MAP : LANG_MAP), [model]);

  const updateChat = useCallback((entry) => {
    setChatHistory((prev) => {
      const next = [...prev, entry];
      chatHistoryRef.current = next;
      return next;
    });
  }, []);
  // ========== BACKGROUND MUSIC MANAGEMENT (IVR STYLE) ==========
  const initializeBackgroundMusic = useCallback(async () => {
    try {
      if (!bgMusicRef.current) {
        const audio = new Audio();

        // Get the relative path from processingMusic import
        let relativePath = processingMusic;

        console.log("[IVR] Initializing background music");

        // Validate processingMusic
        if (!relativePath || typeof relativePath !== "string") {
          console.warn("processingMusic import invalid, trying fallback path");
          relativePath = "/static/media/processing_music.mp3";
        }

        // Convert relative path to absolute URL
        let absoluteUrl = relativePath;
        if (relativePath.startsWith("/")) {
          absoluteUrl = `${window.location.origin}${relativePath}`;
        }

        // Set source on audio element
        audio.src = absoluteUrl;
        audio.loop = true;
        audio.volume = 0;
        audio.preload = "none";
        audio.crossOrigin = "anonymous";
        audio._intentedSrc = absoluteUrl;
        audio._relativePath = relativePath;

        // Event handlers
        audio.addEventListener("error", (e) => {
          const errorCode = audio.error?.code;
          const errorName = ["MEDIA_ERR_ABORTED", "MEDIA_ERR_NETWORK", "MEDIA_ERR_DECODE", "MEDIA_ERR_SRC_NOT_SUPPORTED"][errorCode - 1] || "UNKNOWN";
          console.error("[IVR] Background music error:", { errorCode, errorName, src: audio.src });

          if (audio.networkState === 3 && audio._intentedSrc) {
            setTimeout(() => {
              console.log("[IVR] Attempting recovery");
              audio.src = audio._intentedSrc;
              audio.load();
            }, 1000);
          }
        });

        bgMusicRef.current = audio;
      }
    } catch (e) {
      console.error("[IVR] Failed to initialize background music:", e);
    }
  }, []);

  const startBackgroundMusic = useCallback(async () => {
    if (!bgMusicRef.current) {
      await initializeBackgroundMusic();
    }
    if (!bgMusicRef.current) return;

    // Always try to play if paused, regardless of hasStartedBgMusicRef
    if (bgMusicRef.current.paused) {
      try {
        bgMusicRef.current.volume = 0;
        await bgMusicRef.current.play();
        hasStartedBgMusicRef.current = true;
        console.log("[IVR] Background music playback started/resumed");
      } catch (e) {
        console.warn("[IVR] Background music play failed:", e?.message || e);
      }
    }
  }, [initializeBackgroundMusic]);

  // IVR-style volume control - no pause/play, just volume management
  const setMusicVolume = useCallback((targetVolume) => {
    if (!bgMusicRef.current) return;

    const current = bgMusicRef.current.volume;
    if (Math.abs(current - targetVolume) < 0.01) {
      bgMusicRef.current.volume = targetVolume;
      return;
    }

    // Clear any existing fade
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }

    // Smooth fade
    let volume = current;
    const step = (targetVolume - current) / 10;
    let count = 0;

    fadeIntervalRef.current = setInterval(() => {
      count++;
      volume += step;

      if ((step > 0 && volume >= targetVolume) || (step < 0 && volume <= targetVolume)) {
        bgMusicRef.current.volume = targetVolume;
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      } else {
        bgMusicRef.current.volume = volume;
      }
    }, 30);
  }, []);

  // Helper: Set music for processing state (hold music at medium volume)
  const setProcessingMusicVolume = useCallback(() => {
    setMusicVolume(0.45);
  }, [setMusicVolume]);

  // Helper: Set music for bot speaking (ducked low under bot voice)
  const setBotSpeakingMusicVolume = useCallback(() => {
    setMusicVolume(0.35);
  }, [setMusicVolume]);

  // Helper: Silence music (user input or waiting)
  const silenceMusicVolume = useCallback(() => {
    setMusicVolume(0);
  }, [setMusicVolume]);
  // ========== END BACKGROUND MUSIC MANAGEMENT ==========

  const resetToIdle = () => {
    if (!isMountedRef.current) return;

    setIsProcessing(false);
    isProcessingRef.current = false;
    setIsRecording(false);
    isSendingAudioRef.current = false;
    isStoppingRef.current = false;
    audioChunksRef.current = [];
    recursionDepthRef.current = 0;
    appStateRef.current = "idle";
  };

  const endSessionNow = useCallback((message = "Session ended.") => {
    stopListeningCompletely();
    resetToIdle();
    setIsSessionStarted(false);
    setIsDone(true);
    isDoneRef.current = true;
    setIsSessionCompleted(true);
    setAwaitingThankyouConfirmation(false);
    awaitingThankyouConfirmationRef.current = false;
    setTranscript(message);
    sessionIdRef.current = null;
  }, []);

  const handleOrchestrationResponse = async (orchResponse, langConfig) => {
    if (!orchResponse) {
      setTranscript("Master orchestration failed — please try again.");
      return;
    }

    const responseText =
      orchResponse?.sarvam_text ||
      orchResponse?.baani_text ||
      orchResponse?.final_answer ||
      "";
    const audioBase64 =
      orchResponse?.sarvam_audio ||
      orchResponse?.banni_audio_base64 ||
      "";

    const entry = {
      from: "bot",
      text: responseText || "",
      audio: audioBase64,
      originalAudio: audioBase64,
      model: model || "sarvam",
    };
    updateChat(entry);
    setTranscript(responseText || "");

    // Only play audio in voice mode
    if (audioBase64 && inputModeRef.current === "voice") {
      try {
        setIsAudioPlaying(true);
        setBotSpeakingMusicVolume();
        appStateRef.current = "playing";
        await playBase64Audio(audioBase64);
      } catch (e) {
        err("Master orchestration audio playback failed:", e);
      } finally {
        setIsAudioPlaying(false);
        appStateRef.current = "idle";
      }
    }

    resetToIdle();
    if (!isDoneRef.current && inputModeRef.current === "voice") {
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.start(); } catch (_) { }
      }
      await startPassiveListening();
      setTranscript("Waiting for speech...");
    }
  };

  const connectWebSocket = () => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (wsReconnectAttemptsRef.current >= WS_MAX_RETRIES) {
      warn("WS: max reconnect attempts reached — giving up");
      return;
    }

    wsReadyRef.current = false;
    const wsUrl = buildWsUrl();
    log(`WS: connecting (attempt ${wsReconnectAttemptsRef.current + 1}/${WS_MAX_RETRIES}) → ${wsUrl}`);

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      err("WS: construction failed:", e);
      scheduleWsReconnect();
      return;
    }

    ws.onopen = () => {
      log("WS: connected ✓");
      wsReadyRef.current = true;
      wsReconnectAttemptsRef.current = 0;
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      try {
        ws.send(
          JSON.stringify({
            type: "init",
            session_id: sessionIdRef.current,
            lang: language || "en-IN",
          })
        );
      } catch (e) {
        warn("WS: init send failed", e);
      }
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type === "text" && typeof data.text === "string" && data.text.trim()) {
          setTranscript(data.text);
        }
        if (data.type === "audio" && data.audio) {
          playBase64Audio(data.audio);
        }
      } catch (e) {
        warn("WS: message parse failed", e);
      }
    };

    ws.onerror = (e) => { err("WS: error", e); };

    ws.onclose = (evt) => {
      log("WS: closed", evt.code, evt.reason);
      wsReadyRef.current = false;
      wsRef.current = null;

      if (!isDoneRef.current && inputModeRef.current === "voice") {
        scheduleWsReconnect();
      }
    };

    wsRef.current = ws;
  };

  const scheduleWsReconnect = () => {
    if (wsReconnectAttemptsRef.current >= WS_MAX_RETRIES) return;
    if (wsReconnectTimerRef.current) return;

    wsReconnectAttemptsRef.current += 1;
    const delay = WS_BASE_DELAY_MS * Math.pow(2, wsReconnectAttemptsRef.current - 1);
    log(`WS: scheduling reconnect #${wsReconnectAttemptsRef.current} in ${delay}ms`);

    wsReconnectTimerRef.current = setTimeout(() => {
      wsReconnectTimerRef.current = null;
      if (!isDoneRef.current && inputModeRef.current === "voice") {
        connectWebSocket();
      }
    }, delay);
  };

  const sendAudioChunk = async (blob) => {
    if (!blob || blob.size === 0) return;
    try {
      if (appStateRef.current !== "recording") return;
      if (
        wsRef.current?.readyState === WebSocket.OPEN &&
        wsReadyRef.current &&
        isSendingAudioRef.current
      ) {
        wsRef.current.send(await blob.arrayBuffer());
      }
    } catch (e) {
      warn("sendAudioChunk: failed", e);
    }
  };

  const playBase64Audio = async (base64ArrayOrSingle) => {
    playbackPromiseRef.current = playbackPromiseRef.current.then(async () => {
      const dataList = Array.isArray(base64ArrayOrSingle) ? base64ArrayOrSingle : [base64ArrayOrSingle];

      if (!dataList.length || !dataList.some(d => d)) {
        console.warn("playBase64Audio: no valid audio data provided");
        return;
      }

      setIsAudioPlaying(true);

      // Bot speaking: lower background music (IVR style - don't mute)
      setBotSpeakingMusicVolume();

      // STOP VAD - don't accept user input while bot speaking
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.stop(); } catch (_) { }
      }

      try {
        for (const base64Data of dataList) {
          if (!base64Data) continue;

          // Validate base64 string
          if (typeof base64Data !== "string" || base64Data.length < 100) {
            console.warn("playBase64Audio: invalid base64 data (too short or not string)", base64Data?.length);
            continue;
          }

          // Quick base64 format validation
          if (!/^[A-Za-z0-9+/=]*$/.test(base64Data)) {
            console.warn("playBase64Audio: invalid base64 format (contains invalid characters)");
            continue;
          }

          await new Promise((resolve, reject) => {
            try {
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Uint8Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }

              let mimeType = "audio/wav";
              // Decode first 16 bytes to detect format from header
              const headerBytes = base64Data.substring(0, 24); // ~16 bytes in base64
              try {
                const headerStr = atob(headerBytes);
                // Check for MP3: 0xFF 0xFB or 0xFF 0xFA or ID3 tag
                if (headerStr.charCodeAt(0) === 0xFF && (headerStr.charCodeAt(1) & 0xE0) === 0xE0) {
                  mimeType = "audio/mpeg";
                }
                // Check for RIFF header (WAV/AVI)
                else if (headerStr.charCodeAt(0) === 0x52 && headerStr.charCodeAt(1) === 0x49 &&
                  headerStr.charCodeAt(2) === 0x46 && headerStr.charCodeAt(3) === 0x46) {
                  mimeType = "audio/wav";
                }
                // Check for EBML header (WebM/Matroska)
                else if (headerStr.charCodeAt(0) === 0x1A && headerStr.charCodeAt(1) === 0x45) {
                  mimeType = "audio/webm";
                }
                // Check for Ogg
                else if (headerStr.charCodeAt(0) === 0x4F && headerStr.charCodeAt(1) === 0x67 &&
                  headerStr.charCodeAt(2) === 0x67 && headerStr.charCodeAt(3) === 0x53) {
                  mimeType = "audio/ogg";
                }
                // Check for MP4/M4A (ftyp box)
                else if (headerStr.charCodeAt(4) === 0x66 && headerStr.charCodeAt(5) === 0x74 &&
                  headerStr.charCodeAt(6) === 0x79 && headerStr.charCodeAt(7) === 0x70) {
                  mimeType = "audio/mp4";
                }
              } catch (e) {
                console.warn("Could not detect audio format from header, defaulting to wav", e);
              }

              const audioBlob = new Blob([byteNumbers], { type: mimeType });
              const audioUrl = URL.createObjectURL(audioBlob);
              const audio = new Audio(audioUrl);
              audio.preload = "auto";
              audio.volume = 1.0;
              audio.crossOrigin = "anonymous";
              currentAudioRef.current = audio;

              let audioEnded = false;
              const cleanup = () => {
                if (currentAudioRef.current === audio) currentAudioRef.current = null;
                try { URL.revokeObjectURL(audioUrl); } catch (e) { }
              };

              audio.addEventListener("ended", () => {
                audioEnded = true;
                cleanup();
                resolve();
              });

              audio.addEventListener("error", (e) => {
                console.error("playBase64Audio track error:", e);
                cleanup();
                resolve();
              });

              const timeoutMs = 45000;
              const timeoutId = setTimeout(() => {
                if (!audioEnded) {
                  console.warn("playBase64Audio track timeout");
                  audioEnded = true;
                  cleanup();
                  resolve();
                }
              }, timeoutMs);

              const startPlayback = async () => {
                if (isPaused) {
                  // Wait for resume
                  const check = () => {
                    if (!isPaused) {
                      audio.play().catch((e) => {
                        console.error("playBase64Audio play() error:", e);
                        clearTimeout(timeoutId);
                        cleanup();
                        resolve();
                      });
                    } else {
                      setTimeout(check, 100);
                    }
                  };
                  check();
                } else {
                  audio.play().catch((e) => {
                    console.error("playBase64Audio play() error:", e);
                    clearTimeout(timeoutId);
                    cleanup();
                    resolve();
                  });
                }
              };

              startPlayback();
            } catch (error) {
              console.error("playBase64Audio inner play error:", error);
              resolve();
            }
          });
        }
      } catch (error) {
        console.error("playBase64Audio sequence error:", error);
      } finally {
        setIsAudioPlaying(false);
        isAudioPlayingRef.current = false;
        log("playBase64Audio: finished", {
          inputMode: inputModeRef.current,
          isDone: isDoneRef.current,
          vadExists: Boolean(vadDetectorRef.current),
        });
        if (inputModeRef.current === "voice" && !isDoneRef.current) {
          if (vadDetectorRef.current) {
            log("playBase64Audio: restarting VAD after bot audio");
            try { vadDetectorRef.current.start(); } catch (e) { err("playBase64Audio VAD restart failed:", e); }
          }
          try {
            await startPassiveListening();
          } catch (e) {
            err("playBase64Audio startPassiveListening failed:", e);
          }
        }
      }
    }).catch(err => {
      console.error("Global playback chain error:", err);
      return Promise.resolve();
    });

    return playbackPromiseRef.current;
  };

  const setupAudioContext = (stream) => {
    if (!audioCtxRef.current)
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (!analyserRef.current) {
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
    }
    if (!sourceRef.current) {
      sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
    }
  };

  const setupMediaRecorder = (stream) => {
    try {
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
        } else if (MediaRecorder.isTypeSupported("audio/ogg")) {
          mimeType = "audio/ogg";
        }
      }
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      recordingProcessedRef.current = false;
      audioChunksRef.current = [];
      mediaRecorderRef.current.start();

      const streamInterval = setInterval(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          try { mediaRecorderRef.current.requestData(); } catch (e) { warn("requestData error", e); }
        }
      }, 120);

      mediaRecorderRef.current.onstart = () => {
        log("mediaRecorder: onstart");
        isRecorderReadyRef.current = true;
        isStoppingRef.current = false;
      };

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        audioChunksRef.current.push(e.data);

        if (isSendingAudioRef.current) {
          sendAudioChunk(e.data).catch((ex) => warn("sendAudioChunk error", ex));
        }

        if (!preBufferSentRef.current) {
          preBufferRef.current.push(e.data);
          if (preBufferRef.current.length > 5) preBufferRef.current.shift();
        }

        if (audioChunksRef.current.length > 300) {
          audioChunksRef.current = audioChunksRef.current.slice(-200);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        clearInterval(streamInterval);
        log("mediaRecorder: onstop");
        if (recordingProcessedRef.current) return;

        isSendingAudioRef.current = false;
        preBufferSentRef.current = false;
        isRecorderReadyRef.current = false;
        isStoppingRef.current = false;
        recordingProcessedRef.current = true;
        clearInterval(timerRef.current);

        if (!audioChunksRef.current.length) { setTranscript(""); return; }

        await new Promise((r) => setTimeout(r, 200));
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorderRef.current.mimeType,
        });
        log("audioBlob created — size:", audioBlob.size);

        audioChunksRef.current = [];

        await processRecordingOnStop(audioBlob);
      };
    } catch (e) {
      err("setupMediaRecorder error", e);
      mediaRecorderRef.current = null;
    }
  };

  const flushPreBuffer = async () => {
    if (!preBufferRef.current.length || preBufferSentRef.current) return;
    preBufferSentRef.current = true;
    if (wsRef.current?.readyState === WebSocket.OPEN && wsReadyRef.current) {
      for (const chunk of preBufferRef.current) {
        try { wsRef.current.send(await chunk.arrayBuffer()); } catch (e) { warn("flushPreBuffer send failed", e); }
      }
    }
  };

  const startPassiveListening = async () => {
    log("startPassiveListening: called", {
      passiveListening: passiveListeningRef.current,
      hasVad: !!vadDetectorRef.current,
      hasStream: !!currentStreamRef.current,
      inputMode: inputModeRef.current,
      disableInput: disableInputRef.current,
    });
    if (inputModeRef.current !== "voice") return;

    if (passiveListeningRef.current) {
      log("startPassiveListening: already active");
      if (vadDetectorRef.current) try { vadDetectorRef.current.start(); } catch (_) { }
      wsReconnectAttemptsRef.current = 0;
      return;
    }
    passiveListeningRef.current = true;
    wsReconnectAttemptsRef.current = 0;

    try {
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error("Microphone access requires HTTPS or a secure context.");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      log("startPassiveListening: stream acquired");
      currentStreamRef.current = stream;

      connectWebSocket();
      setupMediaRecorder(stream);
      setupAudioContext(stream);

      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.destroy(); } catch (_) { }
        vadDetectorRef.current = null;
      }

      const vadDetector = new VADDetector({
        threshold: 0.06,
        silenceTimeout: 500,
        smoothing: 0.7,
        useExternalAnalyser: analyserRef.current,

        onSpeechStart: () => {
          try {
            log("VAD: onSpeechStart", {
              appState: appStateRef.current,
              mediaRecorderState: mediaRecorderRef.current?.state,
              isStopping: isStoppingRef.current,
            });
            if (appStateRef.current === "processing" || appStateRef.current === "playing" || isStoppingRef.current) return;
            const rec = mediaRecorderRef.current;
            if (!rec) return;
            if (rec.state === "inactive") {
              log("VAD: restarting inactive recorder");
              recordingProcessedRef.current = false;
              if (!currentStreamRef.current) { warn("No stream for restart"); return; }
              setupMediaRecorder(currentStreamRef.current);
            }
            if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
            isSendingAudioRef.current = true;
            if (!preBufferSentRef.current && preBufferRef.current.length > 0) flushPreBuffer();
            setTranscript("Recording...");
            appStateRef.current = "recording";
            // User speaking: silence the background music
            silenceMusicVolume();
          } catch (e) { err("onSpeechStart error:", e); }
        },

        onSpeechEnd: () => {
          try {
            log("VAD: onSpeechEnd");
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => {
              try {
                isSendingAudioRef.current = false;
                if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "recording") return;
                log("VAD: speech ended, chunkCount", audioChunksRef.current.length);
                if (audioChunksRef.current.length < 2) { log("VAD: too short — ignoring"); return; }
                isStoppingRef.current = true;
                mediaRecorderRef.current.stop();
              } catch (e) { err("VAD onSpeechEnd stop failed:", e); }
            }, 1800);
          } catch (e) { err("onSpeechEnd error:", e); }
        },
      });

      let initialized = await vadDetector.init(stream, analyserRef.current);
      log("VAD: init result with external analyser:", initialized);
      if (!initialized) {
        warn("VAD: external analyser init failed, retrying with internal analyser");
        initialized = await vadDetector.init(stream);
        log("VAD: init result with internal analyser:", initialized);
      }
      if (initialized) {
        vadDetectorRef.current = vadDetector;
        vadDetectorRef.current.start();
        log("VAD: started");
        setVadStatus("listening");
        setIsRecording(true);
      } else {
        warn("VAD: initialization failed, voice detection unavailable");
        passiveListeningRef.current = false;
        setVadStatus("done");
        setIsRecording(false);
      }
    } catch (e) {
      err("startPassiveListening failed:", e);
      setTranscript(e.message || "Microphone access failed — check permissions.");
      passiveListeningRef.current = false;
    }
  };

  const stopListeningCompletely = () => {
    log("stopListeningCompletely");
    passiveListeningRef.current = false;

    if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
    if (wsReconnectTimerRef.current) { clearTimeout(wsReconnectTimerRef.current); wsReconnectTimerRef.current = null; }
    wsReconnectAttemptsRef.current = WS_MAX_RETRIES;

    if (vadDetectorRef.current) { try { vadDetectorRef.current.destroy(); } catch (e) { /* ignore */ } vadDetectorRef.current = null; }
    try { if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }

    if (currentStreamRef.current) {
      try { currentStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
      currentStreamRef.current = null;
    }
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } wsRef.current = null; }
    try {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => { });
        audioCtxRef.current = null;
      }
    } catch (e) { /* ignore */ }
    analyserRef.current = null;
    sourceRef.current = null;

    setIsRecording(false);
    isSendingAudioRef.current = false;
    preBufferSentRef.current = false;
    appStateRef.current = "idle";
    setVadStatus("done");
    // Silence background music when listening stops
    silenceMusicVolume();
  };

  const speakSystemMessage = async (messageKey) => {
    try {
      const activeLang = language || "en-IN";
      const langCode = ACTIVE_LANG_MAP[activeLang]?.code || "en-IN";
      const text = SPEAK_AGAIN_MAP[activeLang] || SPEAK_AGAIN_MAP["en-IN"];

      log("speakSystemMessage:", text, "lang:", langCode, "model:", model);

      let res = null;
      if (model === "sarvam") {
        res = await ttsSarvam(text, langCode);
      } else {
        res = await ttsBani({ text, lang: langCode });
      }

      const audioData = res?.audio_base64 || res?.sarvam_audio_base64 || res?.bhashini_audio_base64 || res?.audio;
      if (audioData) {
        await playBase64Audio(audioData);
      }
    } catch (e) {
      err("speakSystemMessage failed:", e);
    }
  };

  const processRecordingOnStop = async (audioBlob) => {
    log("processRecordingOnStop: awaitingThankyouConfirmationRef", awaitingThankyouConfirmationRef.current);
    const isValid = await validateWebMBlob(audioBlob);
    if (!isValid) {
      warn("Invalid WebM — skipping");
      setShowSpeakAgain(true);
      setTimeout(() => setShowSpeakAgain(false), 3000);
      resetToIdle();
      return;
    }
    if (!passiveListeningRef.current) return;

    try {
      setIsRecording(false);
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setTranscript("Translating...");
      if (vadDetectorRef.current) {
        try { vadDetectorRef.current.stop(); } catch (_) { }
      }
      await startBackgroundMusic();
      setProcessingMusicVolume();

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const res = await processAudioTranslation(audioBlob, langConfig, model);

      if (res.error) {
        if (res.error === "Empty ASR") {
          setShowSpeakAgain(true);
          setTimeout(() => setShowSpeakAgain(false), 5000);
          setIsProcessing(false);
          isProcessingRef.current = false;
          appStateRef.current = "idle";
          const msg = SPEAK_AGAIN_MAP[language] || SPEAK_AGAIN_MAP["en-IN"];
          setTranscript(msg);
          await speakSystemMessage();

          setTimeout(async () => {
            if (!isDoneRef.current && inputModeRef.current === "voice") {
              wsReconnectAttemptsRef.current = 0;
              silenceMusicVolume(); // Silence music before resuming listening
              if (vadDetectorRef.current) {
                try { vadDetectorRef.current.start(); } catch (_) { }
              }
              await startPassiveListening();
              setTranscript("Waiting for speech...");
            }
          }, 300);
          return;
        }
        setIsProcessing(false);
        isProcessingRef.current = false;
        appStateRef.current = "idle";
        setTranscript("Backend error: " + res.error);
        return;
      }

      const { transcriptText } = res;

      if (!transcriptText?.trim()) {
        setIsProcessing(false);
        isProcessingRef.current = false;
        appStateRef.current = "idle";
        const msg = SPEAK_AGAIN_MAP[language] || SPEAK_AGAIN_MAP["en-IN"];
        setTranscript(msg);
        await speakSystemMessage();

        setTimeout(async () => {
          if (!isDoneRef.current && inputModeRef.current === "voice") {
            wsReconnectAttemptsRef.current = 0;
            silenceMusicVolume(); // Silence music before resuming listening
            if (vadDetectorRef.current) {
              try { vadDetectorRef.current.start(); } catch (_) { }
            }
            await startPassiveListening();
            setTranscript("Waiting for speech...");
          }
        }, 300);
        return;
      }

      handleShortReplyDetection(transcriptText);

      const textToSend = sanitizeText(transcriptText);
      setTranscript(textToSend);

      const userEntry = { from: "user", text: textToSend, audio: audioBlob };
      updateChat(userEntry);

      const langCfg = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      if (awaitingThankyouConfirmationRef.current) {
        const yesNoReply = parseYesNoReply(textToSend);
        if (yesNoReply === "yes") {
          setAwaitingThankyouConfirmation(false);
          awaitingThankyouConfirmationRef.current = false;
          const orchResponse = await masterOrchestration({
            session_id: sessionIdRef.current,
            message: textToSend,
            lang: langCfg.backend,
            model,
          });
          await handleOrchestrationResponse(orchResponse, langCfg);
          return;
        }
        if (yesNoReply === "no") {
          endSessionNow("Session closed.");
          return;
        }

        setTranscript("Please say yes or no.");
        setAwaitingThankyouConfirmation(true);
        awaitingThankyouConfirmationRef.current = true;
        resetToIdle();
        return;
      }

      const bookingData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            user_input: textToSend,
            lang: langCfg.backend,
            model,
            mobile_number: selectedMobileNumber,
          }),
        },
        isAskResponseValid,
        3, 1000, 30000
      );

      await handleBotResponse(bookingData, langCfg);
    } catch (e) {
      err("processRecordingOnStop failed:", e);
      resetToIdle();
      setTranscript("Error processing — please try again.");
    } finally {
      resetToIdle();
    }
  };
  const normalizeText = (text = "") =>
    text
      .toLowerCase()
      .normalize("NFKC")
      .replace(/[.,!?।'"`]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const matchesPhrase = (response, phrases, keywords = []) => {
    const text = normalizeText(response);

    // Exact sentence match
    const exactMatch = phrases.some(
      phrase => normalizeText(phrase) === text
    );

    if (exactMatch) return true;

    // Main keyword match
    return keywords.some(
      keyword => text.includes(normalizeText(keyword))
    );
  };

  const handleFollowUpActions = async (originalText, data, type, langConfig) => {
    const text = (originalText || "").toLowerCase();
    const PHRASE_KEYWORDS = {
      goodbye: [
        "thank you for calling",
        "krushi samrudhi helpline",
        "ଧନ୍ୟବାଦ",
        "धन्यवाद",
        "ధన్యవాదాలు"
      ],

      anythingElse: [
        "anything else",
        "any other topic",
        "ଆଉ କୌଣସି ବିଷୟ",
        "किसी और विषय",
        "ఇంకేదైనా విషయం"
      ],

      bookingPending: [
        "booking is being processed",
        "ବୂକିଂଗ୍ କରାଯାଉଛି",
        "बुकिंग की जा रही है",
        "బుకింగ్ చేయబడుతోంది"
      ],

      bookingSuccess: [
        "service confirmation code",
        "booking successful",
        "confirmation code",
        "ସର୍ଭିସ କନଫର୍ମେସନ କୋଡ୍",
        "सेवा पुष्टि कोड",
        "సేవా నిర్ధారణ కోడ్"
      ]
    };

    const goodbyePhrases = [
      "thank you for calling krushi samrudhi helpline! you can call again at any time.",
      "thank you for calling krushi samrudhi helpline! you can call again at any time",
      "thank you for calling krushi samrudhi helpline",
      "కృషి సమృద్ధి హెల్ప్‌లైన్‌కు కాల్ చేసినందుకు ధన్యవాదాలు! మీరు ఎప్పుడైనా మళ్ళీ కాల్ చేయవచ్చు.",
      "కృషి సమృద్ధి హెల్ప్‌లైన్‌కు కాల్ చేసినందుకు ధన్యవాదాలు",
      "कृषि समृद्धि हेल्पलाइन पर कॉल करने के लिए धन्यवाद! आप कभी भी दोबारा कॉल कर सकते हैं।",
      "कृषि समृद्धि हेल्पलाइन पर कॉल करने के लिए धन्यवाद",
      "କୃଷି ସମୃଦ୍ଧି ହେଲ୍ପଲାଇନ୍ କୁ କଲ୍ କରିଥିବାରୁ ଧନ୍ୟବାଦ, ଆପଣ ଯେକୌଣସି ସମୟରେ ପୁଣି କଲ୍ କରିପାରିବେ!",
      "କୃଷି ସମୃଦ୍ଧି ହେଲ୍ପଲାଇନ୍ କୁ କଲ୍ କରିଥିବାରୁ ଧନ୍ୟବାଦ",
    ];

    const anythingElsePhrases = [
      "before we end this call, would you like to know more about anything else? if yes say 'yes', otherwise, say 'no'.",
      "ఈ కాల్‌ను ముగించే ముందు, మీరు ఇంకేదైనా విషయం గురించి తెలుసుకోవాలనుకుంటున్నారా? అయితే 'అవును' అని చెప్పండి, లేకపోతే 'కాదు' అని చెప్పండి.",
      "कॉल समाप्त करने से पहले, क्या आप किसी और विषय के बारे में जानकारी लेना चाहेंगे? यदि हाँ, तो 'हाँ' कहें, अन्यथा 'नहीं' कहें।",
      "ଏହି କଲ୍‌ ଶେଷ କରିବା ପୂର୍ବରୁ, ଆପଣ ଆଉ କୌଣସି ବିଷୟ ବିଷୟରେ ଜାଣିବାକୁ ଚାହାଁନ୍ତି କି? ଯଦି ହଁ, 'ହଁ' କୁହନ୍ତୁ, ନହେଲେ 'ନା' କୁହନ୍ତୁ।",
    ];

    const bookingPendingPhrases = [
      "your cow's ai booking is being processed, please wait a moment.",
      "మీ ఆవు యొక్క ఏ ఐ బుకింగ్ చేయబడుతోంది, దయచేసి కొంత సమయం వేచి ఉండండి.",
      "आपकी गाय की ए आई बुकिंग की जा रही है, कृपया कुछ समय प्रतीक्षा करें।",
      "ଆପଣଙ୍କର ଗାଈ ର ଏ ଆଇ ବୂକିଂగ୍ କରାଯାଉଛି , ଦୟାକରି କିଛି ସମୟ ଅପେକ୍ଷା କରନ୍ତୁ.",
    ];

    const bookingSuccessPhrases = [
      "your ai booking was successful. a service confirmation code has been sent to you. after the service is complete, please give that code to the ai technician.",
      "మీ ఏ ఐ బుకింగ్ విజయవంతమైంది. మీకు ఒక సేవా నిర్ధారణ కోడ్ పంపబడింది. సేవ పూర్తయిన తర్వాత దయచేసి ఏ ఐ టెక్నీషియన్‌కు ఆ కోడ్ ఇవ్వండి.",
      "आपकी ए आई बुकिंग सफल रही। आपको एक सेवा पुष्टि कोड भेजा गया है। सेवा पूरी होने के बाद कृपया ए आई तकनीशियन को वह कोड दें।",
      "ଆପଣଙ୍କର ଏ ଆଇ ବୁକିଂଗ్ ସଫଳ ହୋଇଛି. ଆପଣଙ୍କୁ ଗୋଟିଏ ସର୍ଭିସ କନଫର୍ମେସନ କୋଡ୍ ପଠା ଯାଇଛି . ସେବା ସମ୍ପୁର୍ଣ ହେବା ପରେ ଦୟାକରି ଏ ଆଇ ଟେକ୍ନିସିଆନ୍ ଙ୍କୁ ସେହି କୋଡ ଟି କୁ ଦିଅନ୍ତୁ .",
    ];

    // const isGoodbye = goodbyePhrases.some((phrase) => text.includes(phrase));
    // const isAnythingElse = anythingElsePhrases.some((phrase) => text.includes(phrase));
    // const isBeingprocessed = bookingPendingPhrases.some((phrase) => text.includes(phrase));
    // const isSuccess = bookingSuccessPhrases.some((phrase) => text.includes(phrase)) || data?.ticket_id;
    
    const isGoodbye = matchesPhrase(
      originalText,
      goodbyePhrases,
      PHRASE_KEYWORDS.goodbye
    );

    const isAnythingElse = matchesPhrase(
      originalText,
      anythingElsePhrases,
      PHRASE_KEYWORDS.anythingElse
    );

    const isBeingprocessed = matchesPhrase(
      originalText,
      bookingPendingPhrases,
      PHRASE_KEYWORDS.bookingPending
    );

    const isSuccess =
      matchesPhrase(
        originalText,
        bookingSuccessPhrases,
        PHRASE_KEYWORDS.bookingSuccess
      ) || data?.ticket_id;

    log("Detected:", {isGoodbye,isAnythingElse,isBeingprocessed,isSuccess,originalText});

    if (isAnythingElse && type !== "bye") {
      // Followup question: enable yes/no confirmation and let playback finish resume listening.
      log("handleFollowUpActions: anythingElse detected", {
        awaitingThankyouConfirmation: awaitingThankyouConfirmationRef.current,
        disableInput: disableInputRef.current,
        inputMode: inputModeRef.current,
      });
      setAwaitingThankyouConfirmation(true);
      awaitingThankyouConfirmationRef.current = true;
      disableInputRef.current = false;
      return true;
    }

    if (isGoodbye && type === "followup") {
      // Backend is still transitioning toward session_ended; keep auto-driving the flow.
      followupRetryRef.current += 1;
      log("[GOODBYE] Transition response received on followup, sending 'bye' again", "retry:", followupRetryRef.current);

      if (followupRetryRef.current > 5) {
        log("[GOODBYE] Retry limit reached while awaiting session_ended. Keeping input disabled.");
        return true;
      }

      disableInputRef.current = true;
      try { stopListeningCompletely(); } catch (_) { }

      const followupData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            user_input: "Okay",
            lang: langConfig.backend,
            model,
            mobile_number: selectedMobileNumber,
          }),
        },
        isAskResponseValid
      );

      log("[GOODBYE] Followup response received:", followupData?.ask?.original_text?.substring(0, 100));
      await handleBotResponse(followupData, langConfig, "followup");
      return true;
    }

    if (isSuccess && type !== "bye") {
      // After success, call API again to get the "Anything else?" question
      if (type !== "followup") {
        followupRetryRef.current = 0;
        log("[SUCCESS] First detection - sending 'bye' to backend for followup");
        // Disable user input and stop microphone while followup is fetched
        disableInputRef.current = true;
        try { stopListeningCompletely(); } catch (_) { }

        const followupData = await fetchWithRetry(
          `${API_BASE_URL}/api/collect/ai-booking`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_id: sessionIdRef.current,
              user_input: "bye",
              lang: langConfig.backend,
              model,
              mobile_number: selectedMobileNumber,
            }),
          },
          isAskResponseValid
        );

        log("[SUCCESS] Followup response received:", followupData?.ask?.original_text?.substring(0, 100));
        await handleBotResponse(followupData, langConfig, "followup");
        // prevent outer handler from restarting listening automatically
        return true;
      }

      followupRetryRef.current += 1;
      log(
        "[SUCCESS] RECURSIVE - Backend returned success again for 'bye'.",
        "retry:",
        followupRetryRef.current
      );

      if (followupRetryRef.current < 3) {
        log("[SUCCESS] Retrying bye request to get followup prompt...");
        const followupData = await fetchWithRetry(
          `${API_BASE_URL}/api/collect/ai-booking`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_id: sessionIdRef.current,
              user_input: "bye",
              lang: langConfig.backend,
              model,
              mobile_number: selectedMobileNumber,
            }),
          },
          isAskResponseValid
        );

        log(
          "[SUCCESS] Retry response received:",
          followupData?.ask?.original_text?.substring(0, 100)
        );
        await handleBotResponse(followupData, langConfig, "followup");
        return true;
      }

      log(
        "[SUCCESS] Retry limit reached. Releasing disableInputRef so UI can recover."
      );
      disableInputRef.current = false;
      followupRetryRef.current = 0;
      return false;
    }

    if ((isBeingprocessed) && type !== "followup" && !isSuccess) {
      recursionDepthRef.current++;

      if (recursionDepthRef.current > 5) {
        resetToIdle();
        return true;
      }

      // Disable user input and prevent interruptions during booking processing
      disableInputRef.current = true;
      try { if (vadDetectorRef.current) vadDetectorRef.current.stop(); } catch (_) { }
      try { if (mediaRecorderRef.current && typeof mediaRecorderRef.current.pause === 'function') mediaRecorderRef.current.pause(); } catch (_) { }

      // Keep the mic inactive and play hold music while we poll booking status
      setProcessingMusicVolume();

      const followupData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            user_input: "Ai-booking ticket",
            lang: langConfig.backend,
            model,
            mobile_number: selectedMobileNumber,
          }),
        },
        isAskResponseValid
      );

      await handleBotResponse(followupData, langConfig, "followup");
      // prevent the outer handler from restarting listening automatically
      return true;
    }
    return false;
  };

  const handleBotResponse = async (data, langConfig, type = "normal") => {
    log(`handleBotResponse [${type}]:`, data);
    if (!data) { resetToIdle(); setTranscript("An error occurred — please try again."); return; }

    const { responseText, audioBase64, originalText, botResponseAudio } = extractBotResponse(data, model);

    // Start music on first response (if not already started)
    await startBackgroundMusic();

    if (responseText) {
      const entry = { from: "bot", text: responseText, audio: audioBase64, originalAudio: botResponseAudio, model: model || "sarvam" };
      updateChat(entry);
      setTranscript(responseText);

      // Only play audio in voice mode
      if (audioBase64 && inputModeRef.current === "voice") {
        try {
          setIsAudioPlaying(true);
          setBotSpeakingMusicVolume();
          appStateRef.current = "playing";
          await playBase64Audio(audioBase64);
        } catch (e) {
          err("Response audio playback failed:", e);
        } finally {
          setIsAudioPlaying(false);
          appStateRef.current = "idle";
        }
      }
    }

    const followUpHandled = await handleFollowUpActions(originalText, data, type, langConfig);
    if (followUpHandled) return;

    if (checkIsEnding(originalText) || (!originalText && !responseText)) {
      if (!awaitingThankyouConfirmationRef.current) {
        stopListeningCompletely();
        sessionIdRef.current = null;
        setIsDone(true);
        isDoneRef.current = true;
        setIsSessionStarted(false);
        setIsSessionCompleted(true);
      } else {
        setTranscript("Please say yes or no.");
      }
    }

    resetToIdle();

    // Behavior differs by input mode
    if (!isDoneRef.current) {
      // If input is explicitly disabled (followup/success processing), do not restart listening
      if (disableInputRef.current) {
        log("[EARLY RETURN] Input disabled — not resuming passive listening");
        log("[STATE CHECK] isProcessing:", isProcessingRef.current, "disableInputRef:", disableInputRef.current);
        return;
      }
      if (inputModeRef.current === "voice" && type !== "welcome") {
        wsReconnectAttemptsRef.current = 0;
        // Silence music and resume VAD listening
        silenceMusicVolume();
        if (vadDetectorRef.current) {
          try { vadDetectorRef.current.start(); } catch (_) { }
        }
        await startPassiveListening();
        setTranscript("Waiting for speech...");
      } else if (inputModeRef.current === "text") {
        // In text mode, ensure processing stops and textarea becomes editable
        setIsProcessing(false);
        isProcessingRef.current = false;
        appStateRef.current = "idle";
        // Keep session started so user can type next query
        setIsSessionStarted(true);
        // If we just delivered a prompt, show it; otherwise clear transient status
        // Do not play audio in text mode (handled elsewhere)
      }
    }
  };

  const handleStartSession = async () => {
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    try {
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setIsDone(false);
      isDoneRef.current = false;
      recursionDepthRef.current = 0;

      if (!sessionIdRef.current) {
        sessionIdRef.current = generateUUID();
      }

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const bookingData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionIdRef.current, user_input: "hai", lang: langConfig.backend, model, mobile_number: selectedMobileNumber })
        },
        isAskResponseValid
      );

      if (!bookingData) {
        setIsProcessing(false);
        setTranscript("Failed to start session — please try again.");
        silenceMusicVolume();
        return;
      }

      await handleBotResponse(bookingData, langConfig);
      setIsSessionStarted(true);
    } catch (e) {
      err("handleStartSession failed:", e);
      resetToIdle();
      setTranscript("Failed to start session — please try again.");
      silenceMusicVolume();
    } finally {
      if (!isDoneRef.current) resetToIdle();
    }
  };

  const handleTextSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    if (!inputText.trim()) return;

    setInputMode("text");
    inputModeRef.current = "text";
    stopListeningCompletely();

    const textToSend = sanitizeText(inputText);
    setInputText("");

    if (!textToSend) return;

    const userEntry = { from: "user", text: textToSend };
    updateChat(userEntry);

    try {
      setIsProcessing(true);
      isProcessingRef.current = true;
      appStateRef.current = "processing";
      setTranscript("Processing...");
      recursionDepthRef.current = 0;

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const bookingData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionIdRef.current, user_input: textToSend, lang: langConfig.backend, model, mobile_number: selectedMobileNumber })
        },
        isAskResponseValid
      );

      await handleBotResponse(bookingData, langConfig);
    } catch (e) {
      err("handleTextSubmit failed:", e);
      resetToIdle();
      setTranscript("Error processing request — please try again.");
      silenceMusicVolume();
    } finally {
      if (!isDoneRef.current) resetToIdle();
    }
  };

  const handleStartRecording = async () => {
    if (isMissingSelection) {
      setTranscript("Please select Model, Language & Mobile Number first.");
      return;
    }
    if (appStateRef.current !== "idle") { log("Blocked: system busy"); return; }

    setInputMode("voice");
    inputModeRef.current = "voice";
    setIsDone(false);
    isDoneRef.current = false;
    setIsProcessing(false);
    isProcessingRef.current = false;
    appStateRef.current = "idle";
    recursionDepthRef.current = 0;

    if (isRecording) {
      stopListeningCompletely();
      return;
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = generateUUID();
    }
    wsReconnectAttemptsRef.current = 0;

    setTranscript("");
    setIsRecording(true);
    audioChunksRef.current = [];

    try {
      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      setIsProcessing(true);
      isProcessingRef.current = true;
      const bookingData = await fetchWithRetry(
        `${API_BASE_URL}/api/collect/ai-booking`,
        {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionIdRef.current, user_input: "hai", lang: langConfig.backend, model, mobile_number: selectedMobileNumber })
        },
        isAskResponseValid
      );
      await handleBotResponse(bookingData, langConfig);
    } catch (e) {
      err("handleStartRecording failed:", e);
      setTranscript("Please enable microphone permission and try again.");
      resetToIdle();
      silenceMusicVolume();
    }
  };

  const handleShortReplyDetection = (asrText) => {
    const isShort = checkIsShortReply(asrText);
    if (isShort) {
      isSendingAudioRef.current = false;
      try { vadDetectorRef.current?.stop(); } catch (_) { }
      if (mediaRecorderRef.current?.state === "recording") try { mediaRecorderRef.current.stop(); } catch (_) { }
      audioChunksRef.current = [];
      preBufferSentRef.current = false;
    }
    return isShort;
  };

  const handleReplayMessage = async (index) => {
    const message = chatHistory[index];
    if (!message) return;
    try {
      if (
        currentReplayAudioRef.current &&
        replayingIndex === index &&
        !currentReplayAudioRef.current.paused
      ) {
        currentReplayAudioRef.current.pause();
        setPausedIndex(index);
        return;
      }
      if (
        currentReplayAudioRef.current &&
        replayingIndex === index &&
        currentReplayAudioRef.current.paused
      ) {
        await currentReplayAudioRef.current.play();
        setPausedIndex(null);
        return;
      }
      setReplayingIndex(index);
      setPausedIndex(null);
      let audio;
      if (message.from === "user" && message.audio instanceof Blob) {
        const audioUrl = URL.createObjectURL(message.audio);
        replayUrlRef.current = audioUrl;
        audio = new Audio(audioUrl);
        const cleanup = () => {
          revokeAudioUrl(audioUrl);
          if (replayUrlRef.current === audioUrl) {
            replayUrlRef.current = null;
          }
          currentReplayAudioRef.current = null;
          setReplayingIndex(null);
          setPausedIndex(null);
        };
        currentReplayAudioRef.current = audio;
        audio.addEventListener("ended", cleanup);
        audio.addEventListener("error", cleanup);
        await audio.play();
      } else if (message.from === "bot" && message.audio) {
        const audioUrl = createAudioUrlFromBase64(message.audio);
        audio = new Audio(audioUrl);
        const cleanup = () => {
          revokeAudioUrl(audioUrl);
          currentReplayAudioRef.current = null;
          setReplayingIndex(null);
          setPausedIndex(null);
        };
        currentReplayAudioRef.current = audio;
        audio.addEventListener("ended", cleanup);
        audio.addEventListener("error", cleanup);
        await audio.play();
      }
    } catch (e) {
      err("Replay failed:", e);
      currentReplayAudioRef.current = null;
      setReplayingIndex(null);
      setPausedIndex(null);
    }
  };

  const resetSession = () => {
    stopListeningCompletely();
    setIsSessionStarted(false);
    setIsDone(false);
    isDoneRef.current = false;
    setIsSessionCompleted(false);
    setChatHistory([]);
    chatHistoryRef.current = [];
    sessionIdRef.current = null;
    setIsProcessing(false);
    setIsAudioPlaying(false);
    setIsPaused(false);
    setPausedIndex(null);
    currentAudioRef.current = null;
    currentReplayAudioRef.current = null;
    wsReconnectAttemptsRef.current = 0;
  };

  const handleStartRecordingRef = useRef(null);
  useEffect(() => {
    handleStartRecordingRef.current = handleStartRecording;
  });

  useEffect(() => {
    if (!autoStart) return;
    const t = setTimeout(() => {
      try { handleStartRecordingRef.current?.(); } catch (e) { }
    }, 300);
    return () => clearTimeout(t);
  }, [autoStart]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      revokeAudioUrl(replayUrlRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (wsReconnectTimerRef.current) clearTimeout(wsReconnectTimerRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      try { vadDetectorRef.current?.destroy(); } catch (_) { }
      try { currentStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { }
      try {
        if (bgMusicRef.current) {
          bgMusicRef.current.pause();
          bgMusicRef.current.src = "";
        }
      } catch (_) { }
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.src = "";
        } catch (_) { }
      }
      if (currentReplayAudioRef.current) {
        try {
          currentReplayAudioRef.current.pause();
          currentReplayAudioRef.current.src = "";
        } catch (_) { }
      }
    };
  }, []);

  useEffect(() => {
    if (chatContainerRef.current)
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [chatHistory]);

  useEffect(() => {
    const handleOutsideClick = () => { };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const togglePlayback = useCallback(() => {
    if (!currentAudioRef.current && !bgMusicRef.current) return;

    if (isPaused) {
      setIsPaused(false);
      if (currentAudioRef.current) {
        currentAudioRef.current.play().catch(e => err("Resume failed:", e));
      }
      // If bot was speaking, keep music ducked
      if (isAudioPlaying) setBotSpeakingMusicVolume();
      else if (isProcessing) setProcessingMusicVolume();
    } else {
      setIsPaused(true);
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    }
  }, [isPaused, isAudioPlaying, isProcessing, setBotSpeakingMusicVolume, setProcessingMusicVolume]);

  const getStatusText = () => {
    if (showSpeakAgain) return <span style={{ color: "#ff4d4d" }}>Please speak again...</span>;
    if (isSessionCompleted) return "Process completed for this session";
    if (isAudioPlaying) return isPaused ? "Assistant Paused" : "Assistant is Speaking...";
    if (isRecording) return "Waiting for user input...";
    if (isProcessing) return "Processing...";
    return "Waiting for voice input";
  };

  return (
    <div className="voice-root">
      <div className="bg-decoration">
        <div className="bg-glow-1"></div>
        <div className="bg-glow-2"></div>
      </div>

      <main className="voice-main">
        <div className="studio-container">
          <div className="interaction-studio">
            <div className="studio-header">
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
                <button
                  type="button"
                  className="voice-back-btn"
                  onClick={() => { window.location.href = "/"; }}
                  aria-label="Back to home"
                  style={{ width: 'auto', height: 'auto', borderRadius: '9999px', padding: '8px 16px', gap: '8px', minHeight: '40px' }}
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  <span>Back</span>
                </button>
              </div>
              <div style={{ textAlign: 'center' }}>
                <h3 className="studio-sub-title" style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>Krushi Samrudhi Helpline</h3>
              </div>
              <p className="studio-subtitle" style={{ textAlign: 'center', margin: '10px auto 0 auto', maxWidth: '80%', color: '#4b5563' }}>
                Your direct connection to agricultural expertise. Select your model and start your query.
              </p>
            </div>

            <div className="mode-toggle-wrapper">
              <div className="mode-toggle-pill">
                <button
                  className={`toggle-btn ${inputMode === "voice" ? "active" : ""}`}
                  onClick={() => { setInputMode("voice"); inputModeRef.current = "voice"; resetSession(); }}
                >
                  <span className="material-symbols-outlined">mic</span>
                  Voice Mode
                </button>
                <button
                  className={`toggle-btn ${inputMode === "text" ? "active" : ""}`}
                  onClick={() => { setInputMode("text"); inputModeRef.current = "text"; resetSession(); }}
                >
                  <span className="material-symbols-outlined">keyboard</span>
                  Text Mode
                </button>
              </div>
            </div>

            <div className="studio-card">
              {inputMode === "voice" ? (
                <div className="voice-unit" style={{ animation: "fadeIn 0.4s ease-out" }}>
                  <div className="mic-orb-container">
                    <div className={`orb-pulse ${isRecording ? "active" : ""}`}></div>
                    <button
                      className="mic-orb"
                      onClick={handleStartRecording}
                      disabled={isDisabledMic}
                      aria-label="Start voice interaction"
                    >
                      <span className="material-symbols-outlined">
                        {isRecording ? "graphic_eq" : "mic"}
                      </span>
                      <div className="tap-label">Tap to Speak</div>
                    </button>
                  </div>
                  <div className="status-indicator">
                    <div className="status-control-row" style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "center" }}>
                      <div className="status-text">{getStatusText()}</div>
                      {(isAudioPlaying || replayingIndex !== null) && (
                        <button
                          className="playback-toggle-btn"
                          onClick={togglePlayback}
                          style={{
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "50%",
                            padding: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "white"
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>
                            {isPaused ? "play_circle" : "pause_circle"}
                          </span>
                        </button>
                      )}
                    </div>
                    {awaitingThankyouConfirmation && (
                      <div style={{
                        marginTop: "8px",
                        padding: "8px 12px",
                        borderRadius: "999px",
                        background: "rgba(255, 215, 0, 0.16)",
                        color: "#f9d342",
                        fontSize: "13px",
                        textAlign: "center",
                        boxShadow: "0 0 0 1px rgba(255, 215, 0, 0.22)"
                      }}>
                        Awaiting yes/no confirmation
                      </div>
                    )}
                    {isRecording && (
                      <div className="audio-waves">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="wave-bar active" style={{ animationDelay: `${i * 0.1}s` }} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-unit" style={{ animation: "fadeIn 0.4s ease-out" }}>
                  {isSessionStarted ? (
                    <div className="setting-field">
                      <label className="studio-label">Describe your query</label>
                      <div className="textarea-wrapper">
                        <textarea
                          className="studio-textarea"
                          placeholder="Ex: I want to book insemination process for my cow..."
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          disabled={isProcessing || isDone || isMissingSelection}
                          maxLength={MAX_TEXT_LENGTH}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="start-session-prompt">
                      <div className="start-icon">
                        <span className="material-symbols-outlined">chat</span>
                      </div>
                      <h3>Ready to start your conversation?</h3>
                      <p>Select your AI Model, Language and Mobile number, then click Start to begin.</p>
                    </div>
                  )}
                </div>
              )}

              <div className="input-settings-grid">
                <div className="setting-field">
                  <label className="studio-label">Mobile Number</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={selectedMobileNumber}
                      onChange={(e) => setSelectedMobileNumber(e.target.value)}
                    >
                      <option value="">Select Your Mobile Number</option>
                      {Object.entries(MOBILE_NUMBER).map(([key]) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Village</label>
                  <div className="premium-select-wrapper">
                    <input
                      type="text"
                      className="premium-select"
                      style={{ background: "#f5f5f4", cursor: "default" }}
                      value={MOBILE_NUMBER[selectedMobileNumber] || "Not Registered Number"}
                      readOnly
                    />
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">AI Model</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    >
                      <option value="">Select AI Model</option>
                      {Object.entries(MODEL_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Your Language</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="">Select Language</option>
                      {Object.entries(ACTIVE_LANG_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>
              </div>

              {inputMode === "text" && (
                <div className="studio-footer">
                  <div className="status-badges">
                    <div className="badge badge-emerald">
                      <div className="badge-dot"></div>
                      {isSessionStarted ? "Session Active" : "AI Ready"}
                    </div>
                  </div>
                  {!isSessionStarted ? (
                    <button className="process-button" onClick={handleStartSession} disabled={isStartSessionDisabled}>
                      {isProcessing ? "Starting..." : "Start"}
                      <span className="material-symbols-outlined">play_arrow</span>
                    </button>
                  ) : (
                    <button className="process-button" onClick={handleTextSubmit} disabled={isTextSubmitDisabled}>
                      {isProcessing ? "Processing..." : "Process Query"}
                      <span className="material-symbols-outlined">send</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <aside className="chat-panel-container">
            <div className="chat-panel">
              <div className="chat-header">
                <span>CONVERSATION STREAM</span>
              </div>
              <div className="chat-scroll-area" ref={chatContainerRef}>
                {chatHistory.length === 0 ? (
                  <div className="empty-chat">
                    <span className="material-symbols-outlined" style={{ fontSize: "48px", opacity: 0.2 }}>forum</span>
                    <p>No messages yet</p>
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => (
                    <div key={idx} className={`chat-bubble-wrapper ${msg.from}`}>
                      <div className="chat-bubble" style={msg.from === "bot" || msg.from === "system" ? { textAlign: "justify" } : {}}>
                        {msg.text}
                        {msg.from !== "system" && (
                          <button
                            className={`replay-btn ${replayingIndex === idx ? "playing" : ""}`}
                            onClick={() => handleReplayMessage(idx)}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{ fontSize: "14px" }}
                            >
                              {replayingIndex === idx
                                ? pausedIndex === idx
                                  ? "play_arrow"
                                  : "pause"
                                : "play_circle"}
                            </span>
                            {replayingIndex === idx
                              ? pausedIndex === idx
                                ? "Resume"
                                : "Pause"
                              : "Replay"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

const VoiceRecorder = (props) => (
  <ErrorBoundary>
    <VoiceRecorderInner {...props} />
  </ErrorBoundary>
);

export default VoiceRecorder;





// //============================================ ws ============================================

// import React, { useEffect, useRef, useState, useCallback } from "react";

// // ─── CONFIG ──────────────────────────────────────────────────────────────────
// const WS_URL = "ws://localhost:8525/api/call-info/ai-booking";  // ConVox WebSocket endpoint

// // const WS_URL = "ws://localhost:6424/niruthi/ai-booking";

// // ngrok http --url=dannielle-zonal-defiantly.ngrok-free.dev 80
// const MOBILE_NUMBER = "7008279837";
// const STREAM_SID   = () => `stream-${crypto.randomUUID()}`;
// const CALL_SID     = () => `call-${crypto.randomUUID()}`;
// const ACCOUNT_SID  = "test-account-001";
// const SAMPLE_RATE  = 8000;   // Must match ConVox default (8 kHz)
// const VAD_CHUNK    = 256;    // 256 samples @ 8 kHz = 32 ms chunks

// // ─── HELPERS ─────────────────────────────────────────────────────────────────
// const nowMs  = () => String(Date.now());
// const nowIso = () => new Date().toISOString();

// function pcmToBase64(float32Array) {
//   const int16 = new Int16Array(float32Array.length);
//   for (let i = 0; i < float32Array.length; i++) {
//     const s = Math.max(-1, Math.min(1, float32Array[i]));
//     int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
//   }
//   const bytes = new Uint8Array(int16.buffer);
//   let binary  = "";
//   for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
//   return btoa(binary);
// }

// async function playBase64Audio(base64) {
//   const binary = atob(base64);
//   const bytes  = new Uint8Array(binary.length);
//   for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
//   const ctx    = new (window.AudioContext || window.webkitAudioContext)();
//   const buffer = await ctx.decodeAudioData(bytes.buffer);
//   const src    = ctx.createBufferSource();
//   src.buffer   = buffer;
//   src.connect(ctx.destination);
//   src.start();
//   return new Promise((resolve) => { src.onended = () => { ctx.close(); resolve(); }; });
// }

// // ─── STATUS LABELS ────────────────────────────────────────────────────────────
// const STATUS_LABEL = {
//   idle:       "Idle",
//   connecting: "Connecting…",
//   connected:  "Connected — listening",
//   listening:  "Listening…",
//   processing: "Processing…",
//   speaking:   "Bot speaking…",
//   error:      "Error",
//   closed:     "Disconnected",
// };

// const STATUS_COLOR = {
//   idle:       "#888780",
//   connecting: "#EF9F27",
//   connected:  "#1D9E75",
//   listening:  "#378ADD",
//   processing: "#7F77DD",
//   speaking:   "#D85A30",
//   error:      "#E24B4A",
//   closed:     "#888780",
// };

// // ─── COMPONENT ────────────────────────────────────────────────────────────────
// export default function VoiceRecorder() {
//   const [status,        setStatus]        = useState("idle");
//   const [transcript,    setTranscript]    = useState("");
//   const [assistantText, setAssistantText] = useState("");
//   const [eventLog,      setEventLog]      = useState([]);
//   const [error,         setError]         = useState("");

//   const wsRef          = useRef(null);
//   const mediaStreamRef = useRef(null);
//   const audioCtxRef    = useRef(null);
//   const processorRef   = useRef(null);
//   const streamSidRef   = useRef(STREAM_SID());
//   const callSidRef     = useRef(CALL_SID());
//   const seqRef         = useRef(0);
//   const chunkRef       = useRef(0);
//   const isSpeakingRef  = useRef(false);   // bot is playing audio — ignore mic
//   const logRef         = useRef(null);

//   const addLog = useCallback((dir, event, detail = "") => {
//     setEventLog(prev => [
//       ...prev.slice(-80),   // keep last 80 entries
//       { dir, event, detail, time: new Date().toLocaleTimeString() }
//     ]);
//     setTimeout(() => { logRef.current?.scrollTo(0, logRef.current.scrollHeight); }, 50);
//   }, []);

//   // ── Send helpers ────────────────────────────────────────────────────────────
//   const wsSend = useCallback((obj) => {
//     if (wsRef.current?.readyState === WebSocket.OPEN) {
//       wsRef.current.send(JSON.stringify(obj));
//     }
//   }, []);

//   const sendConnected = useCallback(() => {
//     const msg = { event: "connected" };
//     wsSend(msg);
//     addLog("out", "connected");
//   }, [wsSend, addLog]);

//   const sendStart = useCallback(() => {
//     seqRef.current++;
//     const msg = {
//       event:           "start",
//       sequence_number: seqRef.current,
//       start: {
//         stream_sid:   streamSidRef.current,
//         call_sid:     callSidRef.current,
//         account_sid:  ACCOUNT_SID,
//         from:         MOBILE_NUMBER,
//         to:           "bot-endpoint",
//         custom_parameters: {
//           platform_agent_id: "",
//           public_key:        "",
//           call_id:           null,
//           sample_rate:       SAMPLE_RATE,
//           recording:         false,
//           recording_both:    false,
//         },
//         media_format: { encoding: "audio/x-l16", sample_rate: SAMPLE_RATE },
//       },
//     };
    
//     wsSend(msg);
//     addLog("out", "start", `stream_sid=${streamSidRef.current}`);
//   }, [wsSend, addLog]);

//   const sendMediaChunk = useCallback((base64Payload) => {
//     seqRef.current++;
//     chunkRef.current++;
//     wsSend({
//       event:           "media",
//       sequence_number: seqRef.current,
//       stream_sid:      streamSidRef.current,
//       media: {
//         chunk:     chunkRef.current,
//         timestamp: nowMs(),
//         payload:   base64Payload,
//       },
//     });
//   }, [wsSend]);

//   const sendMark = useCallback(() => {
//     seqRef.current++;
//     wsSend({
//       event:           "mark",
//       sequence_number: seqRef.current,
//       stream_sid:      streamSidRef.current,
//       mark: { name: `audio_played_${chunkRef.current}` },
//     });
//     addLog("out", "mark", `audio_played_${chunkRef.current}`);
//   }, [wsSend, addLog]);

//   const sendClear = useCallback(() => {
//     wsSend({ event: "clear", stream_sid: streamSidRef.current });
//     addLog("out", "clear");
//   }, [wsSend, addLog]);

//   const sendStop = useCallback((reason = "user_hangup") => {
//     seqRef.current++;
//     wsSend({
//       event:           "stop",
//       sequence_number: seqRef.current,
//       stream_sid:      streamSidRef.current,
//       stop: {
//         call_sid:    callSidRef.current,
//         account_sid: ACCOUNT_SID,
//         reason,
//       },
//       timestamp: nowIso(),
//     });
//     addLog("out", "stop", reason);
//   }, [wsSend, addLog]);

//   // ── WebSocket message handler ────────────────────────────────────────────────
//   const handleIncoming = useCallback(async (raw) => {
//     let data;
//     try { data = JSON.parse(raw); } catch { return; }

//     const ev = data.event;
//     addLog("in", ev || "unknown", JSON.stringify(data).slice(0, 120));

//     // Bot sends "media" → play audio back to user
//     if (ev === "media") {
//       const b64 = data.media?.payload;
//       if (!b64) return;
//       isSpeakingRef.current = true;
//       setStatus("speaking");
//       try {
//         await playBase64Audio(b64);
//       } catch (e) {
//         setError("Audio playback failed: " + e.message);
//       }
//       isSpeakingRef.current = false;
//       setStatus("connected");
//       // Tell server audio finished playing
//       sendMark();
//       return;
//     }

//     // Bot sends "stop" → server wants to end call
//     if (ev === "stop") {
//       addLog("in", "stop", data.stop?.reason || "");
//       cleanup();
//       setStatus("closed");
//       return;
//     }

//     // Bot sends "clear" → server interrupted its own audio (barge-in)
//     if (ev === "clear") {
//       isSpeakingRef.current = false;
//       setStatus("connected");
//       return;
//     }
//   }, [addLog, sendMark]);

//   // ── Microphone capture → send media chunks ──────────────────────────────────
//   const startMicCapture = useCallback(() => {
//     if (!mediaStreamRef.current) return;

//     const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
//     audioCtxRef.current = ctx;

//     const source    = ctx.createMediaStreamSource(mediaStreamRef.current);
//     const processor = ctx.createScriptProcessor(VAD_CHUNK, 1, 1);
//     processorRef.current = processor;

//     processor.onaudioprocess = (e) => {
//       // Skip while bot is speaking (prevents echo)
//       if (isSpeakingRef.current) return;
//       if (wsRef.current?.readyState !== WebSocket.OPEN) return;

//       const float32 = e.inputBuffer.getChannelData(0);
//       const b64     = pcmToBase64(float32);
//       sendMediaChunk(b64);
//     };

//     source.connect(processor);
//     processor.connect(ctx.destination);
//   }, [sendMediaChunk]);

//   // ── Cleanup ─────────────────────────────────────────────────────────────────
//   const cleanup = useCallback(() => {
//     try { processorRef.current?.disconnect(); }  catch (_) {}
//     try { audioCtxRef.current?.close(); }        catch (_) {}
//     try { mediaStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
//     try { wsRef.current?.close(); }              catch (_) {}

//     processorRef.current  = null;
//     audioCtxRef.current   = null;
//     mediaStreamRef.current = null;
//     wsRef.current          = null;
//     isSpeakingRef.current  = false;
//     seqRef.current         = 0;
//     chunkRef.current       = 0;
//   }, []);

//   useEffect(() => () => cleanup(), [cleanup]);

//   // ── Connect ─────────────────────────────────────────────────────────────────
//   const startSession = async () => {
//     setError("");
//     setTranscript("");
//     setAssistantText("");
//     setEventLog([]);

//     // Fresh IDs for each session
//     streamSidRef.current = STREAM_SID();
//     callSidRef.current   = CALL_SID();
//     seqRef.current       = 0;
//     chunkRef.current     = 0;

//     try {
//       setStatus("connecting");

//       // 1. Microphone
//       const stream = await navigator.mediaDevices.getUserMedia({
//         audio: { sampleRate: SAMPLE_RATE, channelCount: 1, echoCancellation: true, noiseSuppression: true },
//       });
//       mediaStreamRef.current = stream;

//       // 2. WebSocket
//       const ws = new WebSocket(WS_URL);
//       wsRef.current = ws;

//       ws.onopen = () => {
//         addLog("sys", "ws_open", WS_URL);
//         // Step 1: send connected handshake
//         sendConnected();
//         // Step 2: send start event with call metadata
//         sendStart();
//         // Step 3: begin streaming mic audio
//         startMicCapture();
//         setStatus("connected");
//       };

//       ws.onmessage = (e) => handleIncoming(e.data);

//       ws.onerror = () => {
//         setError("WebSocket connection failed — is the server running?");
//         setStatus("error");
//         cleanup();
//       };

//       ws.onclose = (e) => {
//         addLog("sys", "ws_close", `code=${e.code}`);
//         if (status !== "closed") setStatus("closed");
//         cleanup();
//       };

//     } catch (err) {
//       setError(err?.message || String(err));
//       setStatus("error");
//       cleanup();
//     }
//   };

//   const stopSession = () => {
//     sendStop("user_hangup");
//     cleanup();
//     setStatus("idle");
//   };

//   // ─── RENDER ──────────────────────────────────────────────────────────────────
//   const dot = STATUS_COLOR[status] || "#888";
//   const isRunning = !["idle", "error", "closed"].includes(status);

//   return (
//     <div style={styles.root}>
//       <div style={styles.card}>

//         {/* Header */}
//         <div style={styles.header}>
//           <div style={styles.headerLeft}>
//             <span style={styles.brand}>ConVox</span>
//             <span style={styles.brandSub}>WebSocket Tester</span>
//           </div>
//           <div style={styles.statusPill}>
//             <span style={{ ...styles.statusDot, background: dot }} />
//             <span style={styles.statusTxt}>{STATUS_LABEL[status] || status}</span>
//           </div>
//         </div>

//         {/* Config strip */}
//         <div style={styles.configStrip}>
//           <span style={styles.configItem}><span style={styles.configKey}>WS</span>{WS_URL}</span>
//           <span style={styles.configItem}><span style={styles.configKey}>From</span>{MOBILE_NUMBER}</span>
//           <span style={styles.configItem}><span style={styles.configKey}>Rate</span>{SAMPLE_RATE} Hz</span>
//         </div>

//         {/* IDs */}
//         <div style={styles.idStrip}>
//           <span style={styles.idItem}><span style={styles.configKey}>Stream</span>{streamSidRef.current?.slice(0,18)}…</span>
//           <span style={styles.idItem}><span style={styles.configKey}>Call</span>{callSidRef.current?.slice(0,18)}…</span>
//         </div>

//         {/* Controls */}
//         <div style={styles.controls}>
//           <button style={{ ...styles.btn, ...styles.btnGreen }} onClick={startSession} disabled={isRunning}>
//             ▶ Start Session
//           </button>
//           <button style={{ ...styles.btn, ...styles.btnRed }} onClick={stopSession} disabled={!isRunning}>
//             ■ Stop
//           </button>
//           <button style={{ ...styles.btn, ...styles.btnGray }} onClick={sendClear} disabled={!isRunning}>
//             ✕ Send Clear
//           </button>
//         </div>

//         {error && (
//           <div style={styles.errorBox}>
//             <strong>Error:</strong> {error}
//           </div>
//         )}

//         {/* Transcript + assistant text */}
//         {(transcript || assistantText) && (
//           <div style={styles.responseBox}>
//             {transcript && (
//               <div style={styles.responseRow}>
//                 <span style={styles.responseLabel}>You said</span>
//                 <span style={styles.responseText}>{transcript}</span>
//               </div>
//             )}
//             {assistantText && (
//               <div style={styles.responseRow}>
//                 <span style={{ ...styles.responseLabel, color: "#1D9E75" }}>Bot</span>
//                 <span style={styles.responseText}>{assistantText}</span>
//               </div>
//             )}
//           </div>
//         )}

//         {/* Event log */}
//         <div style={styles.logHeader}>
//           <span>Event log</span>
//           <span style={styles.logCount}>{eventLog.length} events</span>
//         </div>
//         <div style={styles.log} ref={logRef}>
//           {eventLog.length === 0 && (
//             <span style={styles.logEmpty}>No events yet — start a session</span>
//           )}
//           {eventLog.map((e, i) => (
//             <div key={i} style={styles.logRow}>
//               <span style={styles.logTime}>{e.time}</span>
//               <span style={{
//                 ...styles.logTag,
//                 background: e.dir === "in" ? "rgba(55,138,221,.18)" : e.dir === "out" ? "rgba(29,158,117,.18)" : "rgba(136,135,128,.15)",
//                 color:      e.dir === "in" ? "#85B7EB"              : e.dir === "out" ? "#5DCAA5"              : "#B4B2A9",
//               }}>
//                 {e.dir === "in" ? "← IN" : e.dir === "out" ? "→ OUT" : "SYS"}
//               </span>
//               <span style={styles.logEvent}>{e.event}</span>
//               {e.detail && <span style={styles.logDetail}>{e.detail}</span>}
//             </div>
//           ))}
//         </div>

//         {/* Footer hint */}
//         <div style={styles.footer}>
//           Speak after connecting — audio streams automatically as ConVox media events
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─── STYLES ───────────────────────────────────────────────────────────────────
// const styles = {
//   root: {
//     minHeight: "100vh",
//     background: "#0d1117",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     fontFamily: "'IBM Plex Mono', 'Fira Code', monospace",
//     padding: "24px 16px",
//   },
//   card: {
//     width: "100%",
//     maxWidth: 720,
//     background: "#161b27",
//     border: "1px solid #2a3248",
//     borderRadius: 14,
//     overflow: "hidden",
//   },
//   header: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     padding: "16px 20px",
//     borderBottom: "1px solid #2a3248",
//     background: "#1a2035",
//   },
//   headerLeft: { display: "flex", alignItems: "baseline", gap: 10 },
//   brand:    { fontSize: 18, fontWeight: 700, color: "#4f8ef7", letterSpacing: 1 },
//   brandSub: { fontSize: 12, color: "#64748b", letterSpacing: 0.5 },
//   statusPill: {
//     display: "flex", alignItems: "center", gap: 7,
//     background: "#0d1117", borderRadius: 20,
//     padding: "5px 12px", border: "1px solid #2a3248",
//   },
//   statusDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
//   statusTxt: { fontSize: 12, color: "#c8d3e0" },

//   configStrip: {
//     display: "flex", flexWrap: "wrap", gap: 0,
//     borderBottom: "1px solid #2a3248",
//   },
//   configItem: {
//     flex: "1 1 auto",
//     padding: "6px 14px",
//     fontSize: 11,
//     color: "#64748b",
//     borderRight: "1px solid #2a3248",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     whiteSpace: "nowrap",
//   },
//   configKey: {
//     display: "inline-block",
//     marginRight: 6,
//     color: "#4f8ef7",
//     fontWeight: 700,
//     fontSize: 10,
//     letterSpacing: 0.8,
//   },
//   idStrip: {
//     display: "flex", flexWrap: "wrap",
//     borderBottom: "1px solid #2a3248",
//     background: "#11151f",
//   },
//   idItem: {
//     flex: "1 1 auto",
//     padding: "5px 14px",
//     fontSize: 10,
//     color: "#4a5568",
//     borderRight: "1px solid #1e2535",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     whiteSpace: "nowrap",
//   },

//   controls: {
//     display: "flex", gap: 10, padding: "14px 20px",
//     borderBottom: "1px solid #2a3248",
//   },
//   btn: {
//     flex: 1, padding: "9px 0", borderRadius: 7, border: "none",
//     cursor: "pointer", fontSize: 12, fontWeight: 700,
//     fontFamily: "inherit", letterSpacing: 0.5,
//     transition: "opacity .15s, transform .1s",
//   },
//   btnGreen: { background: "#1D9E75", color: "#fff" },
//   btnRed:   { background: "#b91c1c", color: "#fff" },
//   btnGray:  { background: "#1e2535", color: "#85B7EB", border: "1px solid #2a3248" },

//   errorBox: {
//     margin: "0 20px 14px",
//     padding: "10px 14px",
//     background: "rgba(226,75,74,.1)",
//     border: "1px solid rgba(226,75,74,.3)",
//     borderRadius: 8,
//     fontSize: 12,
//     color: "#fca5a5",
//   },

//   responseBox: {
//     margin: "0 20px 14px",
//     background: "#11151f",
//     border: "1px solid #2a3248",
//     borderRadius: 8,
//     overflow: "hidden",
//   },
//   responseRow: {
//     display: "flex", alignItems: "flex-start", gap: 10,
//     padding: "8px 12px",
//     borderBottom: "1px solid #1e2535",
//   },
//   responseLabel: { fontSize: 10, fontWeight: 700, color: "#64748b", minWidth: 48, paddingTop: 1 },
//   responseText:  { fontSize: 13, color: "#c8d3e0", lineHeight: 1.6 },

//   logHeader: {
//     display: "flex", justifyContent: "space-between", alignItems: "center",
//     padding: "8px 20px 6px",
//     fontSize: 11, fontWeight: 700, color: "#4a5568", letterSpacing: 0.8,
//     borderBottom: "1px solid #2a3248",
//     textTransform: "uppercase",
//   },
//   logCount: { fontSize: 10, color: "#2a3248", fontWeight: 400 },
//   log: {
//     height: 260,
//     overflowY: "auto",
//     padding: "8px 12px",
//     display: "flex",
//     flexDirection: "column",
//     gap: 3,
//     scrollBehavior: "smooth",
//   },
//   logEmpty: { fontSize: 11, color: "#2a3248", fontStyle: "italic", padding: "8px 4px" },
//   logRow: { display: "flex", alignItems: "baseline", gap: 8, fontSize: 11 },
//   logTime:   { color: "#2a3248", minWidth: 60, flexShrink: 0 },
//   logTag: {
//     fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
//     letterSpacing: 0.6, flexShrink: 0,
//   },
//   logEvent:  { color: "#c8d3e0", fontWeight: 600, flexShrink: 0 },
//   logDetail: { color: "#4a5568", fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

//   footer: {
//     padding: "8px 20px",
//     fontSize: 10,
//     color: "#2a3248",
//     borderTop: "1px solid #1e2535",
//     textAlign: "center",
//     letterSpacing: 0.4,
//   },
// };
